import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import AuthService from '../services/authservices';
import { DASHBOARD_CONFERENCE_HISTORY, TEAM_ENTITLEMENTS } from '../constants/routes';
import {
  MISSING_CONFERENCE_LABEL,
  fetchConferenceForSeason,
} from '../services/conferenceLookupService';
import {
  fetchJobRosterJson,
  fetchJobScheduleJson,
  fetchJobXmlContent,
  fetchJobXmlFiles,
  fetchMyCompletedJobs,
  MyJobRow,
  XmlFileRow,
} from '../services/jobArtifactsService';
import { DashboardOutletContextValue } from '../types/dashboard';
import Spinner from '../components/Spinner';
import {
  isErrorPayload,
  parseRosterArtifact,
  parseScheduleArtifact,
  RosterPreviewRow,
  SchedulePreviewRow,
} from '../utils/statcrewArtifactPreview';

const authService = new AuthService();

function buildFallbackSeasons(): number[] {
  const y = new Date().getFullYear();
  return Array.from({ length: 24 }, (_, i) => y - i);
}

async function fetchSeasonChoices(teamId: number, sportCode: string): Promise<number[]> {
  const url = new URL(TEAM_ENTITLEMENTS);
  url.searchParams.set('team_id', String(teamId));
  const stored = authService.getStoredUser<any>();
  const token = stored?.access_token || stored?.accessToken;
  const res = await fetch(url.toString(), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) return buildFallbackSeasons();
  const payload = await res.json().catch(() => ({}));
  const sports = Array.isArray(payload?.sports) ? payload.sports : [];
  const row = sports.find((s: { sport_code?: string }) => String(s?.sport_code || '').toUpperCase() === sportCode.toUpperCase());
  const seasonsRaw = row?.seasons;
  if (!Array.isArray(seasonsRaw) || seasonsRaw.length === 0) return buildFallbackSeasons();
  const nums = seasonsRaw
    .map((x: unknown) => parseInt(String(x).replace(/\D/g, ''), 10))
    .filter((n: number) => Number.isFinite(n));
  return nums.length ? Array.from(new Set(nums)).sort((a, b) => b - a) : buildFallbackSeasons();
}

function Modal({
  title,
  onClose,
  children,
  isLoading,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  isLoading?: boolean;
  /** Wider dialog for schedule/roster tables */
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        className={`flex max-h-[85vh] w-full flex-col rounded-2xl bg-white shadow-xl ${wide ? 'max-w-6xl' : 'max-w-4xl'}`}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-sm text-slate-500 hover:bg-slate-100"
          >
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-5">{isLoading ? <Spinner /> : children}</div>
      </div>
    </div>
  );
}

function useAssetPageState() {
  const { selectedSport } = useOutletContext<DashboardOutletContextValue>();
  const stored = authService.getStoredUser<any>();
  const teamId = stored?.team?.id as number | undefined;

  const [season, setSeason] = useState<number>(() => new Date().getFullYear());
  const [seasonChoices, setSeasonChoices] = useState<number[]>([]);
  const [jobs, setJobs] = useState<MyJobRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!teamId) return;
      const opts = await fetchSeasonChoices(teamId, selectedSport || 'MFB');
      if (!active) return;
      setSeasonChoices(opts);
      if (!opts.includes(season)) setSeason(opts[0] ?? season);
    })();
    return () => {
      active = false;
    };
  }, [teamId, selectedSport]);

  const reloadJobs = useCallback(async () => {
    if (!teamId) {
      setError('Missing team. Please login again.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await fetchMyCompletedJobs(season, selectedSport || 'MFB');
      setJobs(list);
    } catch (e) {
      setJobs([]);
      setError(e instanceof Error ? e.message : 'Failed to load jobs.');
    } finally {
      setLoading(false);
    }
  }, [season, selectedSport, teamId]);

  useEffect(() => {
    void reloadJobs();
  }, [reloadJobs]);

  return {
    selectedSport,
    teamId,
    season,
    setSeason,
    seasonChoices,
    jobs,
    loading,
    error,
    reloadJobs,
  };
}

export function StatCrewAssetsXmlPage() {
  const { selectedSport, season, setSeason, seasonChoices, jobs, loading, error } = useAssetPageState();
  const [rows, setRows] = useState<{ job_id: string; path: string; size_bytes: number }[]>([]);
  const [xmlLoading, setXmlLoading] = useState(false);
  const [preview, setPreview] = useState<{ title: string; body: string } | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!jobs.length) {
        setRows([]);
        return;
      }
      setXmlLoading(true);
      const acc: { job_id: string; path: string; size_bytes: number }[] = [];
      try {
        for (const j of jobs) {
          const data = await fetchJobXmlFiles(j.job_id);
          const files = data.xml_files || [];
          for (const f of files) {
            acc.push({ job_id: j.job_id, path: f.path, size_bytes: f.size_bytes });
          }
        }
        if (active) setRows(acc);
      } catch {
        if (active) setRows([]);
      } finally {
        if (active) setXmlLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [jobs]);

  const openPreview = async (jobId: string, path: string) => {
    setPreview({ title: path, body: '' });
    setPreviewLoading(true);
    try {
      const data = await fetchJobXmlContent(jobId, path);
      setPreview({ title: path, body: data.content });
    } catch (e) {
      setPreview({
        title: path,
        body: e instanceof Error ? e.message : 'Could not load XML.',
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-sky-500">{selectedSport}</p>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">XML files (processed jobs)</h1>
          <p className="mt-1 text-sm text-slate-500">ZIP contents for jobs with status Inserted Available.</p>
        </div>
        <label className="flex flex-col text-xs font-semibold uppercase tracking-widest text-slate-500">
          Season
          <select
            value={season}
            onChange={(e) => setSeason(Number(e.target.value))}
            className="mt-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800"
          >
            {seasonChoices.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <p className="mb-4 text-sm text-rose-600">{error}</p>}

      {(loading || xmlLoading) && (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      )}

      {!loading && !xmlLoading && rows.length === 0 && !error && (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white/80 px-6 py-14 text-center text-sm text-slate-500">
          No XML files found for this season and sport. 
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[640px] border-collapse text-left text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="border-b border-slate-200 px-4 py-3 font-semibold text-slate-600">Job</th>
                <th className="border-b border-slate-200 px-4 py-3 font-semibold text-slate-600">File Name</th>
                <th className="border-b border-slate-200 px-4 py-3 font-semibold text-slate-600">Size</th>
                <th className="border-b border-slate-200 px-4 py-3 font-semibold text-slate-600">Preview</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={`${r.job_id}-${r.path}`} className="border-b border-slate-100 hover:bg-sky-50/40">
                  <td className="px-4 py-2 font-mono text-xs text-slate-600">{r.job_id.slice(0, 8)}…</td>
                  <td className="max-w-md truncate px-4 py-2 text-slate-800">{r.path}</td>
                  <td className="px-4 py-2 text-slate-600">
                    {r.size_bytes >= 1024 ? `${(r.size_bytes / 1024).toFixed(1)} KB` : `${r.size_bytes} B`}
                  </td>
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => void openPreview(r.job_id, r.path)}
                      className="rounded-lg bg-cyan-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-600"
                    >
                      Preview
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview && (
        <Modal title={preview.title} onClose={() => setPreview(null)} isLoading={previewLoading}>
          <pre className="max-h-[65vh] overflow-auto whitespace-pre-wrap break-all rounded-xl bg-slate-900 p-4 text-xs text-emerald-100">
            {preview.body}
          </pre>
        </Modal>
      )}
    </div>
  );
}

function SchedulePreviewTable({
  rows,
  conferenceLabel,
}: {
  rows: SchedulePreviewRow[];
  conferenceLabel?: string;
}) {
  const conference = conferenceLabel || MISSING_CONFERENCE_LABEL;
  return (
    <div className="overflow-x-auto overflow-y-auto max-h-[65vh] rounded-xl border border-slate-200">
      <table className="w-full min-w-[640px] border-collapse text-sm">
        <thead className="sticky top-0 z-10 bg-slate-50">
          <tr>
            <th className="w-10 border-b border-slate-200 px-3 py-3 text-center text-xs font-semibold uppercase tracking-widest text-slate-400">
              #
            </th>
            <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">
              Date
            </th>
            <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">
              Opponent
            </th>
            <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">
              Conference
            </th>
            <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">
              Our score
            </th>
            <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">
              Opp. score
            </th>
            <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">
              Match type
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-b border-slate-100 bg-white hover:bg-sky-50/40">
              <td className="px-3 py-2 text-center text-xs text-slate-400">{idx + 1}</td>
              <td className="px-3 py-2 text-slate-800">{row.date || '—'}</td>
              <td className="px-3 py-2 text-slate-800">{row.opponent || '—'}</td>
              <td className="px-3 py-2 text-slate-800">{conference}</td>
              <td className="px-3 py-2 text-slate-800">{row.ourScore || '—'}</td>
              <td className="px-3 py-2 text-slate-800">{row.opponentScore || '—'}</td>
              <td className="px-3 py-2 text-slate-800">{row.matchType || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RosterPreviewTable({ rows }: { rows: RosterPreviewRow[] }) {
  return (
    <div className="overflow-x-auto overflow-y-auto max-h-[65vh] rounded-xl border border-slate-200">
      <table className="w-full min-w-[640px] border-collapse text-sm">
        <thead className="sticky top-0 z-10 bg-slate-50">
          <tr>
            <th className="w-10 border-b border-slate-200 px-3 py-3 text-center text-xs font-semibold uppercase tracking-widest text-slate-400">
              #
            </th>
            <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">
              Class
            </th>
            <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">
              Position
            </th>
            <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">
              First name
            </th>
            <th className="border-b border-slate-200 px-3 py-3 text-left text-xs font-semibold uppercase tracking-widest text-slate-500">
              Family name
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-b border-slate-100 bg-white hover:bg-sky-50/40">
              <td className="px-3 py-2 text-center text-xs text-slate-400">{idx + 1}</td>
              <td className="px-3 py-2 text-slate-800">{row.classYear || '—'}</td>
              <td className="px-3 py-2 text-slate-800">{row.position || '—'}</td>
              <td className="px-3 py-2 text-slate-800">{row.firstName || '—'}</td>
              <td className="px-3 py-2 text-slate-800">{row.familyName || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ArtifactTablePreviewModal({
  title,
  kind,
  data,
  loading,
  onClose,
  conferenceLabel,
  onEditConference,
}: {
  title: string;
  kind: 'schedule' | 'roster';
  data: unknown;
  loading: boolean;
  onClose: () => void;
  conferenceLabel?: string;
  onEditConference?: () => void;
}) {
  const scheduleRows = kind === 'schedule' ? parseScheduleArtifact(data) : [];
  const rosterRows = kind === 'roster' ? parseRosterArtifact(data) : [];

  const rawFallback = useMemo(() => JSON.stringify(data, null, 2), [data]);

  const body = () => {
    if (loading) return null;
    if (isErrorPayload(data)) {
      return <p className="text-sm text-rose-600">{data.error}</p>;
    }
    if (kind === 'schedule') {
      if (scheduleRows.length === 0) {
        return (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">No schedule rows could be parsed from this artifact.</p>
            <details className="rounded-xl border border-slate-200 bg-slate-50">
              <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-slate-600">Raw JSON</summary>
              <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all border-t border-slate-200 p-3 text-xs text-slate-700">
                {rawFallback}
              </pre>
            </details>
          </div>
        );
      }
      return (
        <div className="space-y-3">
          <SchedulePreviewTable rows={scheduleRows} conferenceLabel={conferenceLabel} />
          {onEditConference && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onEditConference}
                className="text-xs font-semibold text-cyan-600 hover:text-cyan-700"
              >
                Edit conference in Conference History
              </button>
            </div>
          )}
        </div>
      );
    }
    if (rosterRows.length === 0) {
      return (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">No roster rows could be parsed from this artifact.</p>
          <details className="rounded-xl border border-slate-200 bg-slate-50">
            <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-slate-600">Raw JSON</summary>
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all border-t border-slate-200 p-3 text-xs text-slate-700">
              {rawFallback}
            </pre>
          </details>
        </div>
      );
    }
    return <RosterPreviewTable rows={rosterRows} />;
  };

  return (
    <Modal title={title} onClose={onClose} isLoading={loading} wide>
      {body()}
    </Modal>
  );
}

export function StatCrewAssetsRosterPage() {
  const { selectedSport, season, setSeason, seasonChoices, jobs, loading, error } = useAssetPageState();
  const [previewJob, setPreviewJob] = useState<string | null>(null);
  const [jsonData, setJsonData] = useState<unknown>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const openPreview = async (jobId: string) => {
    setPreviewJob(jobId);
    setJsonData(null);
    setPreviewLoading(true);
    try {
      const res = await fetchJobRosterJson(jobId);
      setJsonData(res.data);
    } catch {
      setJsonData({ error: 'Failed to load roster JSON.' });
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-sky-500">{selectedSport}</p>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">Roster JSON</h1>
          <p className="mt-1 text-sm text-slate-500">Same artifact as the upload flow (`roster.generated.json`).</p>
        </div>
        <label className="flex flex-col text-xs font-semibold uppercase tracking-widest text-slate-500">
          Season
          <select
            value={season}
            onChange={(e) => setSeason(Number(e.target.value))}
            className="mt-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800"
          >
            {seasonChoices.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <p className="mb-4 text-sm text-rose-600">{error}</p>}
      {loading && (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      )}

      {!loading && jobs.length === 0 && !error && (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white/80 px-6 py-14 text-center text-sm text-slate-500">
          No processed jobs for this season.
        </div>
      )}

      {jobs.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full border-collapse text-left text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="border-b border-slate-200 px-4 py-3 font-semibold text-slate-600">Job ID</th>
                <th className="border-b border-slate-200 px-4 py-3 font-semibold text-slate-600">Updated</th>
                <th className="border-b border-slate-200 px-4 py-3 font-semibold text-slate-600">Preview</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.job_id} className="border-b border-slate-100 hover:bg-sky-50/40">
                  <td className="px-4 py-2 font-mono text-xs">{j.job_id}</td>
                  <td className="px-4 py-2 text-slate-600">{j.updated_at ? String(j.updated_at) : '—'}</td>
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => void openPreview(j.job_id)}
                      className="rounded-lg bg-cyan-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-600"
                    >
                      Preview
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {previewJob && (
        <ArtifactTablePreviewModal
          kind="roster"
          title={`Roster — ${previewJob.slice(0, 8)}…`}
          data={jsonData}
          onClose={() => {
            setPreviewJob(null);
            setJsonData(null);
          }}
          loading={previewLoading}
        />
      )}
    </div>
  );
}

export function StatCrewAssetsSchedulePage() {
  const navigate = useNavigate();
  const { username, selectedSport: outletSport } = useOutletContext<DashboardOutletContextValue>();
  const { selectedSport, season, setSeason, seasonChoices, jobs, loading, error } = useAssetPageState();
  const [previewJob, setPreviewJob] = useState<string | null>(null);
  const [jsonData, setJsonData] = useState<unknown>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [conferenceLabel, setConferenceLabel] = useState<string>(MISSING_CONFERENCE_LABEL);

  const sportCode = selectedSport || outletSport || 'MFB';

  const navigateToConferenceHistory = useCallback(() => {
    const params = new URLSearchParams();
    if (username) params.set('username', username);
    if (sportCode) params.set('selectedSport', sportCode);
    navigate(`${DASHBOARD_CONFERENCE_HISTORY}?${params.toString()}`);
  }, [navigate, sportCode, username]);

  const openPreview = async (jobId: string) => {
    setPreviewJob(jobId);
    setJsonData(null);
    setPreviewLoading(true);
    try {
      const requests: [Promise<{ data: unknown }>, Promise<void>] = [
        fetchJobScheduleJson(jobId),
        (async () => {
          if (!username) {
            setConferenceLabel(MISSING_CONFERENCE_LABEL);
            return;
          }
          try {
            const conf = await fetchConferenceForSeason({
              username,
              selectedSport: sportCode,
              season,
            });
            setConferenceLabel(
              conf.found && conf.displayLabel ? conf.displayLabel : MISSING_CONFERENCE_LABEL
            );
          } catch {
            setConferenceLabel(MISSING_CONFERENCE_LABEL);
          }
        })(),
      ];
      const [res] = await Promise.all(requests);
      setJsonData(res.data);
    } catch {
      setJsonData({ error: 'Failed to load schedule JSON.' });
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-sky-500">{selectedSport}</p>
          <h1 className="mt-1 text-2xl font-semibold text-slate-900">Schedule JSON</h1>
          <p className="mt-1 text-sm text-slate-500">Same artifact as the upload flow (`schedule.generated.json`).</p>
        </div>
        <label className="flex flex-col text-xs font-semibold uppercase tracking-widest text-slate-500">
          Season
          <select
            value={season}
            onChange={(e) => setSeason(Number(e.target.value))}
            className="mt-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800"
          >
            {seasonChoices.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <p className="mb-4 text-sm text-rose-600">{error}</p>}
      {loading && (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      )}

      {!loading && jobs.length === 0 && !error && (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-white/80 px-6 py-14 text-center text-sm text-slate-500">
          No processed jobs for this season.
        </div>
      )}

      {jobs.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full border-collapse text-left text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="border-b border-slate-200 px-4 py-3 font-semibold text-slate-600">Job ID</th>
                <th className="border-b border-slate-200 px-4 py-3 font-semibold text-slate-600">Updated</th>
                <th className="border-b border-slate-200 px-4 py-3 font-semibold text-slate-600">Preview</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.job_id} className="border-b border-slate-100 hover:bg-sky-50/40">
                  <td className="px-4 py-2 font-mono text-xs">{j.job_id}</td>
                  <td className="px-4 py-2 text-slate-600">{j.updated_at ? String(j.updated_at) : '—'}</td>
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => void openPreview(j.job_id)}
                      className="rounded-lg bg-cyan-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-600"
                    >
                      Preview
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {previewJob && (
        <ArtifactTablePreviewModal
          kind="schedule"
          title={`Schedule — ${previewJob.slice(0, 8)}…`}
          data={jsonData}
          conferenceLabel={conferenceLabel}
          onEditConference={navigateToConferenceHistory}
          onClose={() => {
            setPreviewJob(null);
            setJsonData(null);
            setConferenceLabel(MISSING_CONFERENCE_LABEL);
          }}
          loading={previewLoading}
        />
      )}
    </div>
  );
}
